import { useState } from 'react'

interface Column<T> {
  key: keyof T
  label: string
  render?: (value: any, row: T) => React.ReactNode
  width?: string
  sortable?: boolean
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  emptyText?: string
  onRowClick?: (row: T) => void
  selectable?: boolean
  onSelectionChange?: (selected: T[]) => void
}

export function DataTable<T extends { id: string | number }>({
  columns,
  data,
  loading = false,
  emptyText = 'Нет данных',
  onRowClick,
  selectable = false,
  onSelectionChange,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<keyof T | null>(null)
  const [sortAsc, setSortAsc] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const sorted = sortKey
    ? [...data].sort((a, b) => {
        const va = a[sortKey]
        const vb = b[sortKey]
        if (va == null) return 1
        if (vb == null) return -1
        return sortAsc ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
      })
    : data

  const handleSort = (key: keyof T) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const handleSelectAll = () => {
    if (selected.size === data.length) {
      setSelected(new Set())
      onSelectionChange?.([])
    } else {
      const all = new Set(data.map(d => String(d.id)))
      setSelected(all)
      onSelectionChange?.([...data])
    }
  }

  const handleSelect = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
    onSelectionChange?.([...data].filter(d => next.has(String(d.id))))
  }

  if (loading) return <div className="p-4 text-text-muted text-sm">Загрузка...</div>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {selectable && (
              <th className="px-4 py-2 text-left text-xs text-text-muted font-medium w-10">
                <input
                  type="checkbox"
                  checked={data.length > 0 && selected.size === data.length}
                  onChange={handleSelectAll}
                  className="rounded-sm accent-accent"
                />
              </th>
            )}
            {columns.map(col => (
              <th
                key={String(col.key)}
                className={`px-4 py-2 text-left text-xs text-text-muted font-medium ${
                  col.sortable ? 'cursor-pointer select-none' : ''
                } ${col.width ? col.width : ''}`}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                {col.sortable && (
                  <span className="ml-1 opacity-40">
                    {sortKey === col.key ? (sortAsc ? '↑' : '↓') : '↕'}
                  </span>
                )}
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-8 text-center text-text-muted">
                {emptyText}
              </td>
            </tr>
          ) : (
            sorted.map(row => (
              <tr
                key={String(row.id)}
                className={`border-b border-border/50 cursor-pointer transition-colors ${
                  onRowClick ? 'hover:bg-bg-hover' : ''
                } ${selected.has(String(row.id)) ? 'bg-bg-active' : ''}`}
                onClick={() => onRowClick?.(row)}
              >
                {selectable && (
                  <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(String(row.id))}
                      onChange={() => handleSelect(String(row.id))}
                      className="rounded-sm accent-accent"
                    />
                  </td>
                )}
                {columns.map(col => (
                  <td key={String(col.key)} className="px-4 py-2">
                    {col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
